import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import styles from './content-carousel.module.css';
import { fetchImageUrls } from '#/api/images';
import { DisplayItem } from '#/types/message';
import ImageContainer from './image-container/image-container';
import TableDisplay from './table-display/table-display';
import LoadingIndicator from './loading-indicator/loading-indicator';
import VideoContainer from './video-container/video-container';

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider from 'react-slick';

interface ContentCarouselProps {
  items: DisplayItem[] | null;
  onItemClick: (item: DisplayItem, index: number, items: DisplayItem[]) => void;
  analyzingMetadata?: boolean | null;
  verticalLayout?: 'top' | 'bottom';
}

export enum CarouselState {
  INIT = 'INIT',
  ANALYZING = 'ANALYZING',
  LOADING = 'LOADING',
  NO_ITEMS = 'NO_ITEMS',
  DISPLAY = 'DISPLAY',
};

const ContentCarousel: React.FC<ContentCarouselProps> = ({ items, onItemClick, analyzingMetadata, verticalLayout = 'bottom' }) => {

  const [carouselState, setCarouselState] = useState<CarouselState>(CarouselState.INIT);
  const [shouldHide, setShouldHide] = useState(false);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [itemsToProcess, setItemsToProcess] = useState<DisplayItem[]>([]);
  const [itemProcessIndex, setItemProcessIndex] = useState(0);
  const [itemHeights, setItemHeights] = useState<number[]>([]);
  const itemHeightsRef = useRef<number[]>([]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  const analyzisStarted = useRef(false);
  const sliderRef = useRef<Slider>(null);

  const itemsDisplayAreaRef = useRef<HTMLDivElement>(null);
  const [itemsDisplayHeight, setItemsDisplayHeight] = useState<number>(0);

  const getMaxDisplayHeight = () => {
    const screenWidth = window.innerWidth;
    return screenWidth <= 800 ? 470 : 600;
  };

  const [maxDisplayHeight, setMaxDisplayHeight] = useState(getMaxDisplayHeight());

  const loadingIndicatorRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    let rafId: number | null = null;

    const onResize = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setMaxDisplayHeight(getMaxDisplayHeight());
        // poke react-slick to re-measure without dispatching another window event
        // @ts-ignore - innerSlider is not in the public types
        sliderRef.current?.innerSlider?.onWindowResized?.();
      });
    };

    window.addEventListener('resize', onResize, { passive: true });
    onResize(); // initial measure

    return () => {
      window.removeEventListener('resize', onResize);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  const itemsDisplayAreaStyle = useRef<React.CSSProperties>({
    display: "flex",
    flexDirection: 'column',
    justifyContent: verticalLayout === 'top' ? 'flex-start' : 'flex-end',
    alignItems: 'center',
    width: '100%',
    height: `${itemsDisplayHeight}px`,
  });

  useEffect(() => {
    const loadItems = async () => {


      if (analyzingMetadata === true) {
        setCarouselState(CarouselState.ANALYZING);
        analyzisStarted.current = true;
        return;
      }

      if (items && items.length > 0) {
        setCarouselState(CarouselState.LOADING);

        const imageItems = items.filter(item => item.item_type === 'image') as Extract<DisplayItem, { item_type: 'image' }>[];
        const imageIds = imageItems.map(item => item.file_name);
        let imageUrlMap: Record<string, string> = {};

        if (imageIds.length > 0) {
          try {
            imageUrlMap = await fetchImageUrls(imageIds);
          } catch (error) {
          }
        }

        const pItems = items.map((item) => {
          if (item.item_type === 'image') {
            const originalImageItem = item as Extract<DisplayItem, { item_type: 'image' }>;
            const fetchedUrl = imageUrlMap[originalImageItem.file_name] || originalImageItem.file_name;
            return {
              ...originalImageItem,
              file_name: fetchedUrl,
            };
          }
          return item;
        });
        setItemsToProcess(pItems);
      }

      if (analyzisStarted.current && !analyzingMetadata) {
        if ((items === null) || (items && items.length === 0)) {
          setCarouselState(CarouselState.NO_ITEMS);
          return;
        }
        setCarouselState(CarouselState.LOADING);
      }
    };

    loadItems();
  }, [items, analyzingMetadata]);

  useEffect(() => {
    if (carouselState === CarouselState.NO_ITEMS) {
      hideTimeoutRef.current = setTimeout(() => {
        setContainerHeight(0);
        setTimeout(() => setShouldHide(true), 500);
      }, 2500);
    }

    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [carouselState]);

  useEffect(() => {
    if (!loadingIndicatorRef.current) return;

    resizeObserverRef.current = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height;
        if (height > 0) {
          setContainerHeight(verticalLayout === 'top' ? height + 10 : height);
        }
      }
    });

    resizeObserverRef.current.observe(loadingIndicatorRef.current);

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [carouselState]);

  useEffect(() => {
    const maxHeight = Math.max(...itemHeights);
    const shouldAddExtra = itemHeights.length > 1;
    let arrowHeight = 37;
    if (window.innerWidth > 850) {
      arrowHeight = 46;
    }
    if (window.innerWidth < 650) {
      arrowHeight = 32;
    }
    const verticalLayoutHeight = shouldAddExtra ? arrowHeight : 0;

    const totalHeight = maxHeight + verticalLayoutHeight + 4;
    setContainerHeight(totalHeight);
    setItemsDisplayHeight(maxHeight + 2);
    requestAnimationFrame(() => {
      // @ts-ignore - not public API
      sliderRef.current?.innerSlider?.onWindowResized?.();
    });

    itemsDisplayAreaStyle.current = {
      ...itemsDisplayAreaStyle.current,
      height: `${maxHeight}px`,
    };
  }, [itemHeights]);

  const handleHeightReady = (height: number | null) => {
    if (height == null) return;

    itemHeightsRef.current[itemProcessIndex] = height;

    if (itemProcessIndex < itemsToProcess.length - 1) {
      setItemProcessIndex(itemProcessIndex + 1);
    } else {
      // last item, commit
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setItemHeights([...itemHeightsRef.current]);
          setCarouselState(CarouselState.DISPLAY);
          setItemProcessIndex(0);
        });
      });
    }
  };

  const handleItemClick = useCallback((item: DisplayItem, index: number, items: DisplayItem[]) => {
    onItemClick(item, index, items);
  }, [onItemClick]);

  useEffect(() => {
  }, [itemHeights]);


  const currentItem = useMemo(() => {
    return itemsToProcess[itemProcessIndex];
  }, [itemsToProcess, itemProcessIndex]);

  // TODO: TOP LAYOUT FOR THE CHECKLISTS
  const sliderSettings = {
    dots: false,
    arrows: false,
    infinite: itemsToProcess.length > 1,
    speed: 300,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: true,
    afterChange: (index: number) => {
      setCurrentSlideIndex(index);   // <-- keeps in sync with Slick
    },
    beforeChange: () => {
      const allSlides = document.querySelectorAll('.slick-slide');
      allSlides.forEach(slide => {
        const focusableElements = slide.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"]), img[onclick], div[onclick]'
        );
        focusableElements.forEach((element: any) => element.blur?.());
      });
      (document.activeElement as HTMLElement)?.blur?.();
    }
  };

  const goToSlide = (index: number) => {
    sliderRef.current?.slickGoTo(index);
  };

  const goToNext = () => {
    sliderRef.current?.slickNext();
  };

  const goToPrev = () => {
    sliderRef.current?.slickPrev();
  };

  const updateHeightAtIndex = useCallback((index: number, raw: number) => {
    const h = Math.ceil(raw);
    const prev = itemHeightsRef.current[index] ?? 0;
    if (h === prev) return; // nothing changed → no state updates

    itemHeightsRef.current[index] = h;

    // Recompute max only when needed
    const existing = itemHeightsRef.current.filter(Boolean);
    if (existing.length === 0) return;
    const max = Math.max(...existing);
    const extra = (itemsToProcess.length > 1) ? 42 : 0;
    const targetContainer = max + extra;

    // Set only if different
    setItemsDisplayHeight((old) => (old === max ? old : max));
    setContainerHeight((old) => (old === targetContainer ? old : targetContainer));

    // Keep itemHeights in sync without churn
    setItemHeights((prevArr) => {
      const next = [...itemHeightsRef.current]; // sync full snapshot
      if (next.length === prevArr.length && next.every((v, i) => v === prevArr[i])) {
        return prevArr; // identical → no update
      }
      return next;
    });
  }, [itemsToProcess.length]);

  useEffect(() => {
    const id = setInterval(() => {
      const slickIndex = (sliderRef.current?.innerSlider as any)?.state?.currentSlide;
      if (slickIndex != null && slickIndex !== currentSlideIndex) {
        setCurrentSlideIndex(slickIndex);
      }
    }, 300);
    return () => clearInterval(id);
  }, [currentSlideIndex]);

  const renderNavigation = () => {
    if (itemsToProcess.length <= 1) return null;

    return (
      <div className={styles.navigation}>
        <button
          className={`${styles.changeItemButton} ${currentSlideIndex > 0 ? styles.enabled : styles.disabled}`}
          onClick={goToPrev}
          aria-label="Previous item"
        >
          &#10094;
        </button>

        <div className={styles.dots}>
          {itemsToProcess.map((_, index) => (
            <button
              key={index}
              className={`${styles.dot} ${index === currentSlideIndex ? styles.active : ''}`}
              onClick={() => {
                setCurrentSlideIndex(index);     // immediate feedback
                goToSlide(index);                // tell Slick
              }}
            />
          ))}
        </div>

        <button
          className={`${styles.changeItemButton} ${currentSlideIndex < itemsToProcess.length - 1 ? styles.enabled : styles.disabled}`}
          onClick={goToNext}
          aria-label="Next item"
        >
          &#10095;
        </button>
      </div>
    );
  };

  const renderCarousel = () => {
    return (
      <div
        className={`${styles.sliderContainer} ${verticalLayout === 'top' ? styles.topLayout : ''}`}
        style={{
          height: `${containerHeight}px`,
        }}>
        {verticalLayout === 'top' && renderNavigation()}
        <Slider {...sliderSettings} ref={sliderRef}>
          {itemsToProcess.map((item, index) => (
            <div key={index}>
              <div
                className={styles.itemsDisplayArea}
                ref={index === 0 ? itemsDisplayAreaRef : undefined}
                style={{
                  height: `${itemsDisplayHeight}px`,
                  display: 'flex',
                  alignItems: verticalLayout === 'top' ? 'flex-start' : 'flex-end',
                  justifyContent: 'center',
                  margin: '0 auto'
                }}
              >
                {item.item_type === 'image' && (
                  <ImageContainer
                    image={item}
                    onImageClick={() => onItemClick(item, index, itemsToProcess)}
                    maxDisplayWidth={itemsDisplayAreaRef.current?.offsetWidth ?? 0}
                    maxDisplayHeight={maxDisplayHeight}
                    internalActiveIndex={index}
                    onHeightReady={(h) => updateHeightAtIndex(index, h)}
                  />
                )}
                {item.item_type === 'table' && (
                  <TableDisplay
                    table={item}
                    onTableClick={() => onItemClick(item, index, itemsToProcess)}
                    onHeightReady={(h) => updateHeightAtIndex(index, h)}
                  />
                )}
                {item.item_type === 'video' && (
                  <VideoContainer
                    video={item}
                    maxDisplayWidth={itemsDisplayAreaRef.current?.offsetWidth ?? null}
                    maxDisplayHeight={maxDisplayHeight}
                    internalActiveIndex={index}
                  />
                )}
              </div>
            </div>
          ))}
        </Slider>
        {verticalLayout === 'bottom' && renderNavigation()}
      </div>
    )
  }

  return (
    <div
      className={`${styles.chatImageListContainer} ${verticalLayout === 'top' ? styles.topLayout : ''} ${[CarouselState.ANALYZING, CarouselState.LOADING, CarouselState.DISPLAY].includes(carouselState) ||
        (carouselState === CarouselState.NO_ITEMS && !shouldHide)
        ? styles.active
        : styles.inactive
        }`}
      style={{
        height: `${containerHeight}px`
      }}
    >
      {[CarouselState.ANALYZING, CarouselState.NO_ITEMS, CarouselState.LOADING].includes(carouselState) && (
        <LoadingIndicator ref={loadingIndicatorRef} state={carouselState} />
      )}

      {carouselState === CarouselState.DISPLAY && (
        renderCarousel()
      )}

      {/* Hidden calculation elements during LOADING */}
      {carouselState === CarouselState.LOADING && (
        <div
          className={styles.itemsDisplayArea}
          ref={itemsDisplayAreaRef}
          style={{
            ...itemsDisplayAreaStyle.current,
            visibility: 'hidden',
            position: 'absolute',
          }}
        >
          <div style={{ visibility: 'hidden', position: 'absolute' }}>
            {currentItem && currentItem.item_type === 'image' && (
              <ImageContainer
                image={currentItem}
                onImageClick={() => handleItemClick(currentItem, itemProcessIndex, itemsToProcess)}
                maxDisplayWidth={itemsDisplayAreaRef.current?.offsetWidth ?? 0}
                maxDisplayHeight={maxDisplayHeight}
                internalActiveIndex={itemProcessIndex}
                onHeightReady={handleHeightReady}
              />
            )}
            {currentItem && currentItem.item_type === 'table' && (
              <TableDisplay
                table={currentItem}
                onTableClick={() => handleItemClick(currentItem, itemProcessIndex, itemsToProcess)}
                onHeightReady={handleHeightReady}
              />
            )}
            {currentItem && currentItem.item_type === 'video' && (
              <VideoContainer
                video={currentItem}
                maxDisplayWidth={itemsDisplayAreaRef.current?.offsetWidth ?? null}
                maxDisplayHeight={maxDisplayHeight}
                internalActiveIndex={itemProcessIndex}
                onHeightReady={handleHeightReady}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentCarousel;
